import type { FragmentOf, ResultOf } from "@opensea/graphql"
import { graphql, readFragment } from "@opensea/graphql"
import { bn } from "@opensea/utils"

export const SortRewardsByValue_itemFragment = graphql(`
  fragment SortRewardsByValue_item on UserReward {
    __typename
    id
    item {
      bestOffer {
        pricePerItem {
          usd
        }
      }
    }
  }
`)

export const SortRewardsByValue_tokenFragment = graphql(`
  fragment SortRewardsByValue_token on UserTokenReward {
    __typename
    id
    balance {
      usdValue
    }
  }
`)

type ItemReward = FragmentOf<typeof SortRewardsByValue_itemFragment>
type TokenReward = FragmentOf<typeof SortRewardsByValue_tokenFragment>

type SortableReward =
  | ResultOf<typeof SortRewardsByValue_itemFragment>
  | ResultOf<typeof SortRewardsByValue_tokenFragment>

/**
 * Helper function to determine the USD value of a reward
 */
function getRewardValue(reward: SortableReward): number {
  if (reward.__typename === "UserTokenReward") {
    const tokenReward = readFragment(SortRewardsByValue_tokenFragment, reward)
    return bn(tokenReward.balance?.usdValue ?? 0).toNumber()
  }

  // For item rewards, use the best offer USD value
  const itemReward = readFragment(SortRewardsByValue_itemFragment, reward)
  return bn(itemReward.item?.bestOffer?.pricePerItem?.usd ?? 0).toNumber()
}

/**
 * Sorts item and token rewards by their value in descending order.
 * Token rewards are sorted by their USD value.
 * Item rewards are sorted by their best offer USD value.
 *
 * @param itemRewards - Array of item rewards
 * @param tokenRewards - Array of token rewards
 * @returns Sorted array with most valuable rewards first, preserving original fragment types
 */
export function sortRewardsByValue<
  TItem extends ItemReward,
  TToken extends TokenReward,
>(itemRewards: TItem[], tokenRewards: TToken[]): (TItem | TToken)[] {
  // Read fragments for sorting but preserve original objects
  const itemsWithValue = itemRewards.map(r => ({
    original: r as TItem | TToken,
    sortData: readFragment(SortRewardsByValue_itemFragment, r),
  }))

  const tokensWithValue = tokenRewards.map(r => ({
    original: r as TItem | TToken,
    sortData: readFragment(SortRewardsByValue_tokenFragment, r),
  }))

  // Combine and sort
  const allRewards = [...itemsWithValue, ...tokensWithValue].sort((a, b) => {
    const valueA = getRewardValue(a.sortData)
    const valueB = getRewardValue(b.sortData)
    return valueB - valueA // Descending order (highest value first)
  })

  return allRewards.map(({ original }) => original)
}
